import { useEffect, useState } from 'react';
import { Icon } from './Icon';

/* ==========================================================================
   فلترُ الفترة — حبيبةٌ وسهمان ومدًى مكتوب، واحدٌ لكلّ تقارير المبيعات
   ==========================================================================

   كان يعيش في صفحة القمع وحدها، فلمّا طُلب مثلُه في تحليل الخسائر كان
   البابان: نسخُه — فيفترق التوأمان عند أوّل تعديل — أو إخراجُه إلى حيث
   يُشترك فيه. فأُخرج: استايلٌ واحدٌ وسلوكٌ واحدٌ لكلّ من يستعمله.
*/

/**
 * حبيبةُ القياس — سنةٌ أو شهرٌ أو أسبوع.
 *
 * ثلاثةُ أسئلةٍ لا واحد: السنةُ اتجاهُ الشركة، والشهرُ إيقاعُ الفريق،
 * والأسبوعُ نبضُه. ولكلِّ بطاقةٍ حبيبتُها: من يقرأ الاتجاه سنويًّا قد
 * يريد نبضَ أسبوعه في الوقت نفسه.
 */
export type Grain = 'week' | 'month' | 'year';

/** مدى الصفحة: حبيبةٌ بإزاحتها، أو مدًى مكتوبٌ بطرفيه. */
export interface Span {
  grain: Grain;
  offset: number;
  from?: string;
  to?: string;
}

/** ما يُرسل إلى الخادم من المدى — المخصوصُ يُقدَّم إن اكتمل طرفاه. */
export const spanQuery = (sp: Span) =>
  sp.from && sp.to
    ? { from: sp.from, to: sp.to }
    : { bucket: sp.grain, offset: sp.offset };

export const GRAINS: Array<{ key: Grain; label: string; now: string }> = [
  { key: 'week', label: 'أسبوع', now: 'هذا الأسبوع' },
  { key: 'month', label: 'شهر', now: 'هذا الشهر' },
  { key: 'year', label: 'سنة', now: 'هذه السنة' },
];

export function GrainSwitch({
  value,
  onChange,
  withAll = false,
}: {
  value: Grain | null;
  onChange: (g: Grain | null) => void;
  /** خيارُ «الكل» — لبطاقةٍ أصلُها بلا زمن كالقمع. */
  withAll?: boolean;
}) {
  return (
    <span className="seg grain" role="group" aria-label="حبيبة القياس">
      {withAll && (
        <button
          type="button"
          className={`seg-btn${value === null ? ' is-on' : ''}`}
          aria-pressed={value === null}
          onClick={() => onChange(null)}
        >
          الكل
        </button>
      )}
      {GRAINS.map((g) => (
        <button
          key={g.key}
          type="button"
          className={`seg-btn${value === g.key ? ' is-on' : ''}`}
          aria-pressed={value === g.key}
          onClick={() => onChange(g.key)}
        >
          {g.label}
        </button>
      ))}
    </span>
  );
}

/**
 * التنقّل بين الفترات — حبيبةٌ وسهمان واسمُ الفترة بينهما.
 *
 * المفتاحُ وحده يجيب عن «كيف حالنا الآن»، ومن يراجع أداءً يسأل عن شهرٍ
 * مضى أو أسبوعٍ بعينه. فالسهمان يمشيان في الزمن كتقويم: الأقدمُ يمينًا
 * والأحدثُ يسارًا، ولا يُمشى إلى ما بعد اليوم — فترةٌ لم تأتِ لا نتائجَ
 * لها. وتبديلُ الحبيبة يعيد إلى الجارية: «الأسبوع الثالث» لا يقابله
 * «الشهر الثالث» في ذهن أحد.
 */
/**
 * مدًى يكتبه صاحبُه — من تاريخٍ إلى تاريخ.
 *
 * الحبيباتُ الثلاث تجيب عن أسئلةٍ متكرّرة، ويبقى سؤالٌ لا تجيبه: «من
 * أوّل الحملة إلى آخرها»، «من بداية الموسم إلى اليوم». فزرٌّ يفتح
 * حقلين، ولا يُطبَّق حتى يكتمل الطرفان — طرفٌ وحده لا يحدّ مدًى.
 *
 * ولا يجتمع مع حبيبة: اختيارُ إحداهما يلغي الآخر، فمدًى مكتوبٌ وشهرٌ
 * مختارٌ معًا سؤالان لا جواب لهما واحد.
 */
function CustomRange({
  value,
  on,
  onChange,
}: {
  value: { from?: string; to?: string };
  on: boolean;
  onChange: (from?: string, to?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value.from ?? '');
  const [to, setTo] = useState(value.to ?? '');

  useEffect(() => {
    setFrom(value.from ?? '');
    setTo(value.to ?? '');
  }, [value.from, value.to]);

  return (
    <span className="cr">
      <button
        type="button"
        className={`btn btn-sm btn-icon${on ? ' btn-primary' : ''}`}
        title="مدى مخصوص — من تاريخ إلى تاريخ"
        aria-label="مدى مخصوص"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="calendar" size={14} />
      </button>

      {open && (
        <span className="cr-pop">
          <label className="cr-field">
            <span className="tiny mute">من</span>
            <input
              type="date"
              className="input"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="cr-field">
            <span className="tiny mute">إلى</span>
            <input
              type="date"
              className="input"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <span className="cr-acts">
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                onChange(undefined, undefined);
                setOpen(false);
              }}
            >
              إلغاء المدى
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!from || !to || from > to}
              onClick={() => {
                onChange(from, to);
                setOpen(false);
              }}
            >
              تطبيق
            </button>
          </span>
        </span>
      )}
    </span>
  );
}

export function PeriodNav({
  grain,
  onGrain,
  offset,
  onOffset,
  label,
  withAll = false,
  range,
  onRange,
}: {
  grain: Grain | null;
  onGrain: (g: Grain | null) => void;
  offset: number;
  onOffset: (n: number) => void;
  label: string;
  withAll?: boolean;
  /** المدى المكتوب — يُعرض حين يُمرَّر معالجُه. */
  range?: { from?: string; to?: string };
  onRange?: (from?: string, to?: string) => void;
}) {
  const custom = Boolean(range?.from && range?.to);
  return (
    <span className="pn">
      {onRange && (
        <CustomRange value={range ?? {}} on={custom} onChange={onRange} />
      )}
      {/* لا سهامَ لـ«الكل»: ليست فترةً يُتنقَّل بينها. */}
      <span className="pn-move" hidden={grain === null || custom}>
        <button
          type="button"
          className="pn-arrow"
          onClick={() => onOffset(Math.min(60, offset + 1))}
          disabled={offset >= 60}
          title="الفترة السابقة"
          aria-label="الفترة السابقة"
        >
          <Icon name="chevronRight" size={14} />
        </button>
        <span className="pn-label">{label}</span>
        <button
          type="button"
          className="pn-arrow"
          onClick={() => onOffset(Math.max(0, offset - 1))}
          disabled={offset === 0}
          title="الفترة التالية"
          aria-label="الفترة التالية"
        >
          <Icon name="chevronLeft" size={14} />
        </button>
      </span>
      <GrainSwitch
        value={custom ? null : grain}
        withAll={withAll}
        onChange={(g) => {
          /*
           * نداءٌ واحدٌ لا ثلاثة.
           *
           * كان يُنادى «بدّل الحبيبة» ثمّ «صفّر الإزاحة» في نبضةٍ واحدة؛
           * وحالةُ الرأس تعيش في الرابط، والنداءُ الثاني يكتبه من نسخةٍ
           * قديمةٍ فيه الحبيبةُ القديمة — فيمحو الأوّل، ويُضغط «سنة» ولا
           * يتغيّر شيء. فصار نداءً واحدًا، وتصفيرُ الإزاحة ومحوُ المدى من
           * مهمّة المستقبِل في كتبةٍ واحدة.
           */
          onGrain(g);
        }}
      />
    </span>
  );
}

/**
 * حبيبةُ بطاقةٍ تتبع الرئيسَ ثمّ تنفرد.
 *
 * الفلترُ في رأس الصفحة يقود الكلَّ — من غيّره أراد الصفحةَ كلَّها على
 * فترته. ومن أراد بعدها بطاقةً وحدها على فترةٍ أخرى — «كيف كان أغسطسُ
 * هنا وأنا أقرأ سبتمبر؟» — بدّلها في مكانها، حتى يُغيَّر الرئيسُ مرّةً
 * أخرى فتعود الصفحةُ صفًّا واحدًا.
 */
export interface CardSpan {
  grain: Grain;
  setGrain: (g: Grain) => void;
  offset: number;
  setOffset: (n: number) => void;
  /** المدى المكتوب في البطاقة نفسها إن كُتب. */
  range: { from?: string; to?: string };
  setRange: (from?: string, to?: string) => void;
  /** ما يُرسل: مدى البطاقة، وإلّا مدى الرأس، وإلّا حبيبتُها. */
  span: Span;
}

export function useSyncedGrain(master: Span): CardSpan {
  const [grain, setGrain] = useState<Grain>(master.grain);
  const [offset, setOffset] = useState(master.offset);
  const [ownRange, setOwnRange] = useState<{ from?: string; to?: string }>({});
  const [own, setOwn] = useState(false);

  /* تغييرُ الرأس يعيد البطاقةَ إليه ويمحو انفرادَها. */
  useEffect(() => {
    setGrain(master.grain);
    setOffset(master.offset);
    setOwnRange({});
    setOwn(false);
  }, [master.grain, master.offset, master.from, master.to]);

  /*
   * ثلاثةٌ بترتيبها: مدى البطاقة إن كُتب فيها، ثمّ مدى الرأس ما لم تنفرد
   * البطاقةُ بحبيبة، ثمّ حبيبتُها. فمن كتب مدًى في بطاقةٍ أراده لها وحدها،
   * ومن كتبه في الرأس أراده للصفحة.
   */
  const span: Span =
    ownRange.from && ownRange.to
      ? { grain, offset, from: ownRange.from, to: ownRange.to }
      : !own && master.from && master.to
        ? master
        : { grain, offset };

  return {
    grain,
    setGrain: (g) => {
      setOwn(true);
      setOwnRange({});
      setGrain(g);
      /* «الأسبوع الثالث» لا يقابله «الشهر الثالث» في ذهن أحد. */
      setOffset(0);
    },
    offset,
    setOffset: (n) => {
      setOwn(true);
      setOffset(n);
    },
    range: ownRange.from ? ownRange : { from: master.from, to: master.to },
    setRange: (from, to) => {
      setOwn(true);
      setOwnRange(from && to ? { from, to } : {});
    },
    span,
  };
}
